/**
 * <s-style-goals> owns the Gift Shop campaign page: shopping mode, style-goal
 * selection, budget filter, the device-stored Style Card (separate from the
 * Shopify cart), buying a saved look, share links, shared-card rendering, the
 * PNG download and campaign analytics.
 *
 * Root attributes (written by Liquid):
 *   data-products-url  routes.root_url + 'products/' (for live /products/<handle>.js lookups)
 *   data-page-url      canonical page URL used for share links
 *   data-locale        request locale
 * Children: [data-gs-strings] JSON of UI strings, [data-gs-goals] JSON of goal copy.
 *
 * Share links carry only goal, year, optional display name and handle/variant/qty
 * triples. Prices and stock are always re-fetched live.
 */
const STORAGE_KEY = 'arag-style-card-v1';
const MAX_ITEMS = 20;
const MAX_QTY = 10;
const HANDLE_RE = /^[a-z0-9][a-z0-9-]{0,254}$/;

class SStyleGoals extends HTMLElement {
  #ac = new AbortController();
  #state = { mode: 'them', goal: 1, name: '', message: '', items: [] };
  #strings = {};
  #goals = [];
  #budget = 0;
  #busy = false;
  #shared = null;

  connectedCallback() {
    const signal = this.#ac.signal;
    this.#strings = decodeStrings(readJson(this.querySelector('[data-gs-strings]')) || {});
    this.#goals = readJson(this.querySelector('[data-gs-goals]')) || [];
    this.year = this.querySelector('[data-gs-picker]')?.dataset.year || '2027';
    this.#load();

    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('input', (event) => this.#onInput(event), { signal });
    this.addEventListener('change', (event) => this.#onChange(event), { signal });

    this.#formatBudgets();
    this.#applyMode(this.#state.mode, false);
    this.#applyGoal(this.#state.goal, false);
    this.#syncSaveButtons();
    this.#renderCard();
    this.#revalidateSaved();
    this.#openSharedFromUrl();
    track('gift_shop_viewed', { year: this.year });
  }

  disconnectedCallback() {
    this.#ac.abort();
  }

  // ---------- state ----------
  #load() {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (raw && typeof raw === 'object') {
        this.#state.mode = raw.mode === 'me' ? 'me' : 'them';
        this.#state.goal = clampGoal(raw.goal);
        this.#state.name = String(raw.name || '').slice(0, 24);
        this.#state.message = String(raw.message || '').slice(0, 140);
        this.#state.items = Array.isArray(raw.items) ? raw.items.filter(validItem).slice(0, MAX_ITEMS) : [];
      }
    } catch { /* storage unavailable: start fresh */ }
    const nameInput = this.querySelector('[data-gs-name]');
    if (nameInput) nameInput.value = this.#state.name;
    const message = this.querySelector('[data-gs-message]');
    if (message) message.value = this.#state.message;
  }

  #save() {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.#state)); } catch { /* ignore */ }
  }

  // ---------- events ----------
  #onClick(event) {
    const t = event.target instanceof Element ? event.target : null;
    if (!t) return;
    const modeBtn = t.closest('[data-gs-mode-btn]');
    if (modeBtn) return this.#applyMode(modeBtn.dataset.gsModeBtn, true);
    const setMode = t.closest('[data-gs-set-mode]');
    if (setMode) { this.#applyMode(setMode.dataset.gsSetMode, true); return; }
    const goalBtn = t.closest('[data-gs-goal-btn]');
    if (goalBtn) return this.#applyGoal(Number(goalBtn.dataset.gsGoalBtn), true);
    const budgetBtn = t.closest('[data-gs-budget]');
    if (budgetBtn) return this.#applyBudget(Number(budgetBtn.dataset.gsBudget));
    const saveBtn = t.closest('[data-gs-save]');
    if (saveBtn) return this.#saveFromCard(saveBtn);
    const remove = t.closest('[data-gs-remove]');
    if (remove) return this.#removeItem(Number(remove.dataset.gsRemove));
    const step = t.closest('[data-gs-step]');
    if (step) return this.#stepQty(Number(step.dataset.index), Number(step.dataset.gsStep));
    if (t.closest('[data-gs-message-use]')) return this.#useSuggestion();
    if (t.closest('[data-gs-message-clear]')) return this.#setMessage('');
    if (t.closest('[data-gs-buy]')) return this.#buyLook();
    if (t.closest('[data-gs-share]') || t.closest('[data-gs-hint]')) return this.#share();
    if (t.closest('[data-gs-copy]')) return this.#copyLink();
    if (t.closest('[data-gs-download]')) return this.#download();
    if (t.closest('[data-gs-shared-add-all]')) return this.#addShared(null);
    const sharedAdd = t.closest('[data-gs-shared-add]');
    if (sharedAdd) return this.#addShared(Number(sharedAdd.dataset.gsSharedAdd));
  }

  #onInput(event) {
    const t = event.target;
    if (!(t instanceof HTMLElement)) return;
    if (t.matches('[data-gs-name]')) {
      this.#state.name = t.value.slice(0, 24);
      this.#save();
      this.#renderTitle();
    } else if (t.matches('[data-gs-message]')) {
      this.#setMessage(t.value, false);
    }
  }

  #onChange(event) {
    const t = event.target;
    if (!(t instanceof HTMLSelectElement)) return;
    if (t.matches('[data-gs-item-variant]')) {
      const item = this.#state.items[Number(t.dataset.index)];
      const variant = item?.variants?.find((v) => String(v.id) === t.value);
      if (!item || !variant) return;
      item.variantId = variant.id;
      item.variantTitle = variant.title;
      item.price = variant.price;
      item.available = variant.available !== false;
      this.#save();
      this.#renderCard();
    } else if (t.matches('[data-gs-variant]')) {
      const card = t.closest('[data-gs-card]');
      const feedback = card?.querySelector('[data-gs-feedback]');
      if (feedback) feedback.textContent = '';
    }
  }

  // ---------- mode / goal / budget ----------
  #applyMode(mode, user) {
    const next = mode === 'me' ? 'me' : 'them';
    this.#state.mode = next;
    this.dataset.gsMode = next;
    this.querySelectorAll('[data-gs-mode-btn]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.gsModeBtn === next)));
    this.#renderTitle();
    if (user) {
      this.#save();
      track('gift_shop_mode_selected', { mode: next });
    }
  }

  #applyGoal(goal, user) {
    const n = clampGoal(goal);
    this.#state.goal = n;
    this.querySelectorAll('[data-gs-goal-btn]').forEach((b) => b.setAttribute('aria-checked', String(Number(b.dataset.gsGoalBtn) === n)));
    this.querySelectorAll('[data-gs-goal-panel], [data-gs-goal-grid]').forEach((el) => {
      const match = Number(el.dataset.gsGoalPanel || el.dataset.gsGoalGrid) === n;
      el.toggleAttribute('data-gs-hidden', !match);
    });
    const g = this.#goal(n);
    const suggestion = this.querySelector('[data-gs-message-suggestion]');
    if (suggestion) suggestion.textContent = g.message || '';
    this.#renderGoalOnCard();
    this.#renderRecs();
    this.#applyBudget(this.#budget, false);
    if (user) {
      this.#save();
      track('gift_shop_goal_selected', { goal: g.title || String(n) });
    }
  }

  #formatBudgets() {
    this.querySelectorAll('[data-gs-budget]').forEach((btn) => {
      const usd = Number(btn.dataset.gsBudget);
      const label = btn.querySelector('[data-gs-budget-text]');
      if (!usd || !label || !this.#strings.budget_under) return;
      label.textContent = this.#strings.budget_under.replace('{{ amount }}', this.#money(this.#toPresentment(usd * 100), true));
    });
  }

  #toPresentment(usdCents) {
    const rate = Number(window.Shopify?.currency?.rate || 1);
    return Math.round(usdCents * (Number.isFinite(rate) && rate > 0 ? rate : 1));
  }

  #applyBudget(usd, user = true) {
    this.#budget = usd || 0;
    this.querySelectorAll('[data-gs-budget]').forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.gsBudget) === this.#budget)));
    const limit = this.#budget ? this.#toPresentment(this.#budget * 100) : Infinity;
    this.querySelectorAll('[data-gs-grid]').forEach((grid) => {
      let shown = 0;
      grid.querySelectorAll('[data-gs-item]').forEach((li) => {
        const price = Number(li.querySelector('[data-gs-card]')?.dataset.price || 0);
        const visible = price <= limit;
        li.hidden = !visible;
        if (visible) shown += 1;
      });
      const empty = grid.querySelector('[data-gs-empty]');
      if (empty) empty.hidden = shown > 0;
    });
    if (user) track('gift_shop_budget_selected', { budget_usd: this.#budget || 'all' });
  }

  #goal(n) {
    return this.#goals[clampGoal(n) - 1] || { title: '', forecast: '', message: '' };
  }

  // ---------- saving ----------
  #saveFromCard(btn) {
    const card = btn.closest('[data-gs-card]');
    if (!card) return;
    const feedback = card.querySelector('[data-gs-feedback]');
    const select = card.querySelector('[data-gs-variant]');
    let variantId;
    let variantTitle = '';
    let price;
    let variants = [];
    if (select instanceof HTMLSelectElement) {
      if (!select.value) {
        if (feedback) feedback.textContent = this.#strings.choose_first || '';
        select.focus();
        return;
      }
      const opt = select.selectedOptions[0];
      variantId = Number(select.value);
      variantTitle = opt?.dataset.title || '';
      price = Number(opt?.dataset.price || 0);
      variants = [...select.options].filter((o) => o.value).map((o) => ({ id: Number(o.value), title: o.dataset.title || '', price: Number(o.dataset.price || 0), available: true }));
    } else {
      variantId = Number(card.dataset.variantId);
      price = Number(card.dataset.variantPrice || 0);
    }
    if (!variantId) return;

    const existing = this.#state.items.findIndex((i) => i.variantId === variantId);
    if (existing === -1) {
      if (this.#state.items.length >= MAX_ITEMS) return;
      const wasEmpty = this.#state.items.length === 0;
      this.#state.items.push({
        handle: card.dataset.handle,
        title: card.dataset.title,
        url: card.dataset.url,
        image: card.dataset.image,
        variantId,
        variantTitle,
        price,
        qty: 1,
        variants,
        available: true,
      });
      if (wasEmpty) track('gift_shop_card_created', { goal: this.#goal(this.#state.goal).title });
      track('gift_shop_item_saved', { handle: card.dataset.handle, variant_id: variantId });
      flyToCard(card, this.querySelector('[data-gs-card-visual]'));
    }
    this.#save();
    if (feedback) feedback.textContent = this.#strings.saved_toast || '';
    this.#syncSaveButtons();
    this.#renderCard();
  }

  #removeItem(index) {
    const [removed] = this.#state.items.splice(index, 1);
    if (removed) track('gift_shop_item_removed', { handle: removed.handle, variant_id: removed.variantId });
    this.#save();
    this.#syncSaveButtons();
    this.#renderCard();
  }

  #stepQty(index, delta) {
    const item = this.#state.items[index];
    if (!item) return;
    item.qty = Math.min(MAX_QTY, Math.max(1, item.qty + delta));
    this.#save();
    this.#renderCard();
  }

  #syncSaveButtons() {
    const saved = new Set(this.#state.items.map((i) => i.handle));
    this.querySelectorAll('[data-gs-card]').forEach((card) => {
      const btn = card.querySelector('[data-gs-save]');
      if (!btn) return;
      const isSaved = saved.has(card.dataset.handle);
      btn.setAttribute('aria-pressed', String(isSaved));
      btn.textContent = isSaved ? this.#strings.saved || btn.textContent : this.#strings.save || btn.textContent;
    });
  }

  // ---------- message ----------
  #useSuggestion() {
    this.#setMessage(this.#goal(this.#state.goal).message || '');
  }

  #setMessage(value, writeInput = true) {
    this.#state.message = String(value).slice(0, 140);
    const input = this.querySelector('[data-gs-message]');
    if (writeInput && input) input.value = this.#state.message;
    const count = this.querySelector('[data-gs-message-count]');
    if (count) count.textContent = `${this.#state.message.length}/140`;
    this.#save();
    this.#renderMessagePreview();
  }

  // ---------- card rendering ----------
  #renderTitle() {
    const el = this.querySelector('[data-gs-card-title]');
    if (!el) return;
    el.textContent = this.#cardTitle(this.#state.name, this.#state.mode);
  }

  #cardTitle(name, mode) {
    const s = this.#strings;
    const tpl = name ? s.card_title_named : mode === 'me' ? s.card_title_me : s.card_title_them;
    return (tpl || '').replace('{{ name }}', name).replace('{{ year }}', this.year);
  }

  #renderGoalOnCard() {
    const g = this.#goal(this.#state.goal);
    const goalEl = this.querySelector('[data-gs-card-goal]');
    const forecastEl = this.querySelector('[data-gs-card-forecast]');
    if (goalEl) goalEl.textContent = g.title || '';
    if (forecastEl) {
      forecastEl.textContent = g.forecast || '';
      forecastEl.classList.remove('gs-pop');
      void forecastEl.offsetWidth;
      forecastEl.classList.add('gs-pop');
    }
  }

  #renderMessagePreview() {
    const el = this.querySelector('[data-gs-card-message-preview]');
    if (!el) return;
    el.textContent = this.#state.message;
    el.hidden = !this.#state.message || this.#state.mode === 'me';
  }

  #renderCard() {
    const list = this.querySelector('[data-gs-card-list]');
    const empty = this.querySelector('[data-gs-card-empty]');
    const items = this.#state.items;
    const s = this.#strings;
    if (list) {
      list.replaceChildren(...items.map((item, index) => this.#itemRow(item, index)));
    }
    if (empty) empty.hidden = items.length > 0;
    const subtotal = items.filter((i) => i.available !== false).reduce((sum, i) => sum + i.price * i.qty, 0);
    const subtotalEl = this.querySelector('[data-gs-card-subtotal]');
    if (subtotalEl) subtotalEl.textContent = items.length ? this.#money(subtotal) : '—';
    const count = items.reduce((sum, i) => sum + i.qty, 0);
    const preview = this.querySelector('[data-gs-preview-count]');
    if (preview) preview.textContent = items.length ? (s.card_count || '').replace('{{ count }}', String(count)) : s.card_empty || '';
    const sticky = this.querySelector('[data-gs-sticky]');
    if (sticky) sticky.hidden = items.length === 0;
    const stickyCount = this.querySelector('[data-gs-sticky-count]');
    if (stickyCount) stickyCount.textContent = String(count);
    const buy = this.querySelector('[data-gs-buy]');
    if (buy) buy.disabled = items.length === 0 || this.#busy;
    this.querySelectorAll('[data-gs-share], [data-gs-copy], [data-gs-download], [data-gs-hint]').forEach((b) => { b.disabled = items.length === 0; b.classList.toggle('opacity-50', items.length === 0); });
    this.#renderTitle();
    this.#renderGoalOnCard();
    this.#renderMessagePreview();
    this.#renderRecs();
    const count2 = this.querySelector('[data-gs-message-count]');
    if (count2) count2.textContent = `${this.#state.message.length}/140`;
  }

  #itemRow(item, index) {
    const s = this.#strings;
    const li = el('li', 'flex gap-3 py-3');
    const img = el('img', 'size-16 shrink-0 object-cover bg-surface-secondary');
    img.src = item.image || '';
    img.alt = '';
    img.width = 64;
    img.height = 64;
    img.loading = 'lazy';
    const body = el('div', 'flex min-w-0 grow flex-col gap-1');
    const title = el('a', 'type-body-sm font-semibold text-on-surface hover:underline underline-offset-4 line-clamp-2');
    title.href = item.url || '#';
    title.textContent = item.title;
    body.append(title);
    if (item.variants && item.variants.length > 1) {
      const select = el('select', 'h-9 w-full max-w-60 border border-border bg-surface px-2 text-xs text-on-surface');
      select.dataset.gsItemVariant = '';
      select.dataset.index = String(index);
      select.setAttribute('aria-label', (s.card_variant || '').replace('{{ title }}', item.title));
      item.variants.forEach((v) => {
        const opt = new Option(`${v.title} · ${this.#money(v.price)}`, String(v.id), false, v.id === item.variantId);
        opt.disabled = v.available === false;
        select.append(opt);
      });
      body.append(select);
    } else if (item.variantTitle) {
      body.append(text('p', 'text-xs text-on-surface-secondary', item.variantTitle));
    }
    if (item.available === false) body.append(text('p', 'text-xs font-semibold text-sale', s.card_unavailable || ''));
    const controls = el('div', 'flex items-center gap-2');
    const minus = btn('−', 'size-8 border border-border text-on-surface', (s.card_qty || '').replace('{{ title }}', item.title) + ' −');
    minus.dataset.gsStep = '-1';
    minus.dataset.index = String(index);
    const qty = text('span', 'min-w-6 text-center text-sm', String(item.qty));
    qty.setAttribute('aria-label', (s.card_qty || '').replace('{{ title }}', item.title));
    const plus = btn('+', 'size-8 border border-border text-on-surface', (s.card_qty || '').replace('{{ title }}', item.title) + ' +');
    plus.dataset.gsStep = '1';
    plus.dataset.index = String(index);
    controls.append(minus, qty, plus);
    body.append(controls);
    const side = el('div', 'flex flex-col items-end justify-between gap-2');
    side.append(text('p', 'text-sm font-semibold text-on-surface', this.#money(item.price * item.qty)));
    const remove = btn('×', 'size-8 text-lg leading-none text-on-surface-secondary hover:text-on-surface', (s.card_remove || '').replace('{{ title }}', item.title));
    remove.dataset.gsRemove = String(index);
    side.append(remove);
    li.append(img, body, side);
    return li;
  }

  #renderRecs() {
    const wrap = this.querySelector('[data-gs-recs]');
    const list = this.querySelector('[data-gs-recs-list]');
    if (!wrap || !list) return;
    const saved = new Set(this.#state.items.map((i) => i.handle));
    const grid = this.querySelector(`[data-gs-goal-grid="${this.#state.goal}"]`);
    const cards = grid ? [...grid.querySelectorAll('[data-gs-card]')].filter((c) => !saved.has(c.dataset.handle)).slice(0, 3) : [];
    wrap.hidden = this.#state.items.length === 0 || cards.length === 0;
    list.replaceChildren(...cards.map((c) => {
      const li = el('li', 'flex flex-col gap-1');
      const a = el('a', 'flex flex-col gap-1 text-on-surface no-underline hover:underline underline-offset-4');
      a.href = `#${c.querySelector('form')?.id || ''}`;
      const img = el('img', 'aspect-square w-full object-cover bg-surface-secondary');
      img.src = c.dataset.image || '';
      img.alt = '';
      img.loading = 'lazy';
      a.append(img, text('span', 'text-xs font-semibold line-clamp-2', c.dataset.title || ''), text('span', 'text-xs text-on-surface-secondary', this.#money(Number(c.dataset.price || 0))));
      li.append(a);
      return li;
    }));
  }

  // ---------- live data ----------
  async #fetchProduct(handle) {
    const base = this.dataset.productsUrl || '/products/';
    const res = await fetch(`${base}${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(String(res.status));
    return res.json();
  }

  async #revalidateSaved() {
    const items = this.#state.items;
    if (!items.length) return;
    await Promise.all(items.map(async (item) => {
      try {
        const p = await this.#fetchProduct(item.handle);
        applyLive(item, p);
      } catch {
        item.available = false;
      }
    }));
    this.#save();
    this.#renderCard();
  }

  // ---------- buying ----------
  async #addLines(lines) {
    if (!window.Shopify?.actions?.updateCart) throw new Error('unavailable');
    const result = await window.Shopify.actions.updateCart({ lines }, { event: { context: 'product', detail: { source: 's-style-goals' } } });
    const message = result?.userErrors?.[0]?.message;
    if (message) throw new Error(message);
    await window.Shopify.actions.openCart?.();
  }

  // Line item property for the gift message (For Them mode only). Key names the recipient when given.
  #giftLineAttribute() {
    const msg = this.#state.message.trim();
    if (this.#state.mode !== 'them' || !msg) return null;
    const s = this.#strings;
    const name = this.#state.name.trim();
    const key = name ? (s.note_prefix_named || '').replace('{{ name }}', name) : s.note_prefix || '';
    return { key: key || 'Gift message', value: msg };
  }

  async #buyLook() {
    if (this.#busy || !this.#state.items.length) return;
    const status = this.querySelector('[data-gs-buy-status]');
    const s = this.#strings;
    this.#busy = true;
    this.#renderCard();
    if (status) status.textContent = s.buy_busy || '';
    track('gift_shop_buy_look_clicked', { mode: this.#state.mode, items: this.#state.items.length });
    try {
      await this.#revalidateSaved();
      const ok = this.#state.items.filter((i) => i.available !== false);
      const bad = this.#state.items.filter((i) => i.available === false);
      if (!ok.length) {
        if (status) status.textContent = s.buy_none || '';
        return;
      }
      // The gift message rides on the first item as a line item property so it shows at checkout
      // and on the order. Only one line carries it, so it doesn't repeat across the look.
      const giftLine = this.#giftLineAttribute();
      const lines = ok.map((i, index) => {
        const line = { merchandiseId: String(i.variantId), quantity: i.qty };
        if (giftLine && index === 0) line.attributes = [giftLine];
        return line;
      });
      await this.#addLines(lines);
      if (giftLine) track('gift_shop_message_added', {});
      track('gift_shop_added_to_cart', { source: 'style_card', lines: ok.length });
      if (status) status.textContent = bad.length ? (s.buy_partial || '').replace('{{ items }}', bad.map((i) => i.title).join(', ')) : '';
    } catch (error) {
      if (status) status.textContent = error instanceof Error && error.message && error.message !== 'unavailable' ? error.message : s.buy_error || '';
    } finally {
      this.#busy = false;
      this.#renderCard();
    }
  }

  // ---------- sharing ----------
  #shareUrl() {
    const payload = { v: 1, y: this.year, g: this.#state.goal, i: this.#state.items.map((i) => [i.handle, i.variantId, i.qty]) };
    if (this.#state.name) payload.n = this.#state.name;
    const url = new URL(this.dataset.pageUrl || window.location.href, window.location.origin);
    url.search = '';
    url.hash = 'style-card';
    url.searchParams.set('card', encodeCard(payload));
    return url.toString();
  }

  async #share() {
    if (!this.#state.items.length) return;
    const url = this.#shareUrl();
    const s = this.#strings;
    track('gift_shop_share_clicked', { mode: this.#state.mode });
    if (navigator.share) {
      try {
        await navigator.share({ title: this.#cardTitle(this.#state.name, this.#state.mode), text: s.share_text || '', url });
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
      }
    }
    await this.#copyLink(url);
  }

  async #copyLink(prebuilt) {
    if (!this.#state.items.length) return;
    const url = typeof prebuilt === 'string' ? prebuilt : this.#shareUrl();
    const status = this.querySelector('[data-gs-share-status]');
    const field = this.querySelector('[data-gs-share-url]');
    try {
      await navigator.clipboard.writeText(url);
      if (status) status.textContent = this.#strings.copied || '';
      track('gift_shop_link_copied', {});
    } catch {
      if (field instanceof HTMLInputElement) {
        field.hidden = false;
        field.value = url;
        field.select();
      }
      if (status) status.textContent = this.#strings.copy_failed || '';
    }
  }

  #openSharedFromUrl() {
    const raw = new URLSearchParams(window.location.search).get('card');
    if (!raw) return;
    const data = decodeCard(raw);
    const panel = this.querySelector('[data-gs-shared]');
    const invalid = this.querySelector('[data-gs-shared-invalid]');
    if (!data || !panel) {
      if (invalid) invalid.hidden = false;
      return;
    }
    this.#shared = { ...data, items: data.i.map(([handle, variantId, qty]) => ({ handle, variantId, qty, available: false, loaded: false })) };
    const s = this.#strings;
    const title = this.querySelector('[data-gs-shared-title]');
    if (title && data.n) title.textContent = (s.shared_heading_named || '').replace('{{ name }}', data.n).replace('{{ year }}', data.y);
    const g = this.#goal(data.g);
    const goalEl = this.querySelector('[data-gs-shared-goal]');
    const forecastEl = this.querySelector('[data-gs-shared-forecast]');
    if (goalEl) goalEl.textContent = g.title || '';
    if (forecastEl) forecastEl.textContent = g.forecast || '';
    panel.hidden = false;
    this.#applyGoal(data.g, false);
    this.#renderShared();
    Promise.all(this.#shared.items.map(async (item) => {
      try {
        const p = await this.#fetchProduct(item.handle);
        applyLive(item, p);
      } catch {
        item.available = false;
      }
      item.loaded = true;
    })).then(() => this.#renderShared());
    panel.closest('section')?.scrollIntoView({ block: 'start' });
  }

  #renderShared() {
    const list = this.querySelector('[data-gs-shared-list]');
    if (!list || !this.#shared) return;
    const s = this.#strings;
    list.replaceChildren(...this.#shared.items.map((item, index) => {
      const li = el('li', 'flex items-center gap-3 py-3');
      const img = el('img', 'size-16 shrink-0 object-cover bg-surface-secondary');
      img.src = item.image || '';
      img.alt = '';
      img.width = 64;
      img.height = 64;
      const body = el('div', 'flex min-w-0 grow flex-col gap-0.5');
      const title = el('a', 'type-body-sm font-semibold text-on-surface hover:underline underline-offset-4');
      title.href = item.url || '#';
      title.textContent = item.title || item.handle;
      body.append(title);
      if (item.variantTitle) body.append(text('p', 'text-xs text-on-surface-secondary', item.variantTitle));
      body.append(text('p', 'text-xs text-on-surface-secondary', `× ${item.qty}${item.loaded && item.available ? ` · ${this.#money(item.price * item.qty)}` : ''}`));
      if (item.loaded && !item.available) body.append(text('p', 'text-xs font-semibold text-sale', s.card_unavailable || ''));
      const add = btn(s.add_to_bag || 'Add', 'button-outline h-10 px-3 type-overline text-[11px] rounded-button shrink-0', `${s.add_to_bag || ''}: ${item.title || item.handle}`);
      add.dataset.gsSharedAdd = String(index);
      add.disabled = !item.loaded || !item.available;
      add.classList.toggle('opacity-50', add.disabled);
      li.append(img, body, add);
      return li;
    }));
    const addAll = this.querySelector('[data-gs-shared-add-all]');
    if (addAll) addAll.disabled = !this.#shared.items.some((i) => i.loaded && i.available);
  }

  async #addShared(index) {
    if (this.#busy || !this.#shared) return;
    const status = this.querySelector('[data-gs-shared-status]');
    const s = this.#strings;
    const picked = index === null ? this.#shared.items : [this.#shared.items[index]].filter(Boolean);
    this.#busy = true;
    if (status) status.textContent = s.buy_busy || '';
    try {
      await Promise.all(picked.map(async (item) => {
        try { applyLive(item, await this.#fetchProduct(item.handle)); } catch { item.available = false; }
      }));
      this.#renderShared();
      const ok = picked.filter((i) => i.available);
      const bad = picked.filter((i) => !i.available);
      if (!ok.length) {
        if (status) status.textContent = s.buy_none || '';
        return;
      }
      await this.#addLines(ok.map((i) => ({ merchandiseId: String(i.variantId), quantity: i.qty })));
      track('gift_shop_added_to_cart', { source: 'shared_card', lines: ok.length });
      if (status) status.textContent = bad.length ? (s.buy_partial || '').replace('{{ items }}', bad.map((i) => i.title || i.handle).join(', ')) : '';
    } catch (error) {
      if (status) status.textContent = error instanceof Error && error.message && error.message !== 'unavailable' ? error.message : s.buy_error || '';
    } finally {
      this.#busy = false;
    }
  }

  // ---------- PNG export ----------
  async #download() {
    if (!this.#state.items.length) return;
    const status = this.querySelector('[data-gs-share-status]');
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1350;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('no canvas');
      const css = getComputedStyle(this);
      const bg = css.getPropertyValue('--color-surface').trim() || '#F2F0E9';
      const ink = css.getPropertyValue('--color-on-surface').trim() || '#19291E';
      const lime = css.getPropertyValue('--gs-lime').trim() || '#D5EF79';
      const heading = css.getPropertyValue('--font-heading').trim() || 'sans-serif';
      const body = css.getPropertyValue('--font-body').trim() || 'sans-serif';
      await document.fonts?.ready;
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, 1080, 1350);
      ctx.strokeStyle = ink;
      ctx.lineWidth = 8;
      ctx.strokeRect(40, 40, 1000, 1270);
      ctx.fillStyle = ink;
      ctx.font = `700 30px ${body}`;
      ctx.fillText((this.#strings.card_branding || '2027 Style Goals').toUpperCase(), 90, 120);
      ctx.font = `800 64px ${heading}`;
      wrapText(ctx, this.#cardTitle(this.#state.name, this.#state.mode).toUpperCase(), 90, 200, 900, 70, 2);
      const g = this.#goal(this.#state.goal);
      ctx.font = `600 34px ${body}`;
      ctx.fillText(g.title || '', 90, 360);
      // forecast stamp
      ctx.save();
      ctx.translate(90, 400);
      ctx.rotate(-0.04);
      ctx.font = `800 26px ${body}`;
      const fw = Math.min(900, ctx.measureText((g.forecast || '').toUpperCase()).width + 40);
      ctx.fillStyle = lime;
      ctx.fillRect(0, 0, fw, 56);
      ctx.strokeStyle = ink;
      ctx.lineWidth = 4;
      ctx.strokeRect(0, 0, fw, 56);
      ctx.fillStyle = ink;
      ctx.fillText((g.forecast || '').toUpperCase(), 20, 38);
      ctx.restore();
      // product images grid (up to 6)
      const shown = this.#state.items.slice(0, 6);
      const cols = shown.length <= 2 ? shown.length : 3;
      const size = cols ? Math.floor((900 - (cols - 1) * 20) / cols) : 0;
      const imgs = await Promise.all(shown.map((i) => loadImage(i.image)));
      imgs.forEach((im, idx) => {
        const x = 90 + (idx % cols) * (size + 20);
        const y = 500 + Math.floor(idx / cols) * (size + 70);
        ctx.fillStyle = '#E8E6DC';
        ctx.fillRect(x, y, size, size);
        if (im) drawCover(ctx, im, x, y, size, size);
        ctx.fillStyle = ink;
        ctx.font = `600 22px ${body}`;
        wrapText(ctx, shown[idx].title, x, y + size + 30, size, 26, 1);
      });
      if (this.#state.message && this.#state.mode === 'them') {
        ctx.font = `italic 28px ${body}`;
        ctx.fillStyle = ink;
        wrapText(ctx, `“${this.#state.message}”`, 90, 1170, 900, 36, 2);
      }
      ctx.font = `800 24px ${heading}`;
      ctx.fillText((this.#strings.closing || 'SET THE TONE FROM THE CROWN DOWN.').toUpperCase(), 90, 1270);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('export failed');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `style-card-${this.year}.png`;
      document.body.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      if (status) status.textContent = '';
    } catch {
      if (status) status.textContent = this.#strings.download_failed || '';
    }
  }

  // ---------- helpers ----------
  #money(cents, whole = false) {
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(this.dataset.locale || undefined, { style: 'currency', currency, maximumFractionDigits: whole ? 0 : 2, minimumFractionDigits: whole ? 0 : 2 }).format((cents || 0) / 100);
    } catch {
      return `${((cents || 0) / 100).toFixed(2)} ${currency}`;
    }
  }
}

// Translation strings arrive HTML-escaped (e.g. &#39;). Decode once so textContent shows real characters.
function decodeStrings(strings) {
  const scratch = document.createElement('textarea');
  const out = {};
  for (const [key, value] of Object.entries(strings)) {
    if (typeof value !== 'string') continue;
    scratch.innerHTML = value;
    out[key] = scratch.value;
  }
  return out;
}

function readJson(node) {
  try { return node ? JSON.parse(node.textContent || 'null') : null; } catch { return null; }
}

function clampGoal(n) {
  const v = Number(n);
  return Number.isInteger(v) && v >= 1 && v <= 4 ? v : 1;
}

function validItem(i) {
  return i && typeof i === 'object' && HANDLE_RE.test(String(i.handle || '')) && Number.isFinite(Number(i.variantId)) && Number(i.qty) >= 1;
}

function applyLive(item, p) {
  const variant = (p.variants || []).find((v) => Number(v.id) === Number(item.variantId));
  item.title = p.title;
  item.url = p.url;
  item.variants = (p.variants || []).length > 1 ? p.variants.map((v) => ({ id: v.id, title: v.title, price: v.price, available: v.available })) : [];
  const img = variant?.featured_image?.src || p.featured_image;
  if (img) item.image = sizedImage(img);
  if (!variant) {
    item.available = false;
    return;
  }
  item.variantTitle = (p.variants || []).length > 1 ? variant.title : '';
  item.price = variant.price;
  item.available = Boolean(variant.available);
}

function sizedImage(src) {
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', '400');
    return url.toString();
  } catch {
    return src;
  }
}

function encodeCard(payload) {
  const bytes = new TextEncoder().encode(JSON.stringify(payload));
  let bin = '';
  bytes.forEach((b) => { bin += String.fromCharCode(b); });
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decodeCard(raw) {
  try {
    if (raw.length > 4000) return null;
    const b64 = raw.replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64 + '==='.slice((b64.length + 3) % 4));
    const data = JSON.parse(new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0))));
    if (!data || data.v !== 1 || !Array.isArray(data.i) || !data.i.length) return null;
    const items = data.i.slice(0, MAX_ITEMS).filter((t) => Array.isArray(t) && HANDLE_RE.test(String(t[0])) && /^\d{1,20}$/.test(String(t[1])))
      .map((t) => [String(t[0]), Number(t[1]), Math.min(MAX_QTY, Math.max(1, Number.parseInt(String(t[2]), 10) || 1))]);
    if (!items.length) return null;
    return { y: String(data.y || '').slice(0, 4), g: clampGoal(data.g), n: typeof data.n === 'string' ? data.n.slice(0, 24) : '', i: items };
  } catch {
    return null;
  }
}

function el(tag, className) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

function text(tag, className, value) {
  const node = el(tag, className);
  node.textContent = value;
  return node;
}

function btn(label, className, aria) {
  const node = el('button', className);
  node.type = 'button';
  node.textContent = label;
  if (aria) node.setAttribute('aria-label', aria);
  return node;
}

function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function drawCover(ctx, img, x, y, w, h) {
  const r = Math.max(w / img.width, h / img.height);
  const sw = w / r;
  const sh = h / r;
  ctx.drawImage(img, (img.width - sw) / 2, (img.height - sh) / 2, sw, sh, x, y, w, h);
}

function wrapText(ctx, value, x, y, maxWidth, lineHeight, maxLines) {
  const words = String(value).split(/\s+/);
  let line = '';
  let lines = 0;
  for (let i = 0; i < words.length; i += 1) {
    const test = line ? `${line} ${words[i]}` : words[i];
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, y + lines * lineHeight);
      lines += 1;
      line = words[i];
      if (lines >= maxLines) return;
    } else {
      line = test;
    }
  }
  if (line && lines < maxLines) ctx.fillText(line, x, y + lines * lineHeight);
}

function flyToCard(card, target) {
  if (!target || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const img = card.querySelector('img');
  if (!img) return;
  const from = img.getBoundingClientRect();
  const to = target.getBoundingClientRect();
  if (to.bottom < 0 || to.top > window.innerHeight) return;
  const ghost = img.cloneNode();
  Object.assign(ghost.style, { position: 'fixed', left: `${from.left}px`, top: `${from.top}px`, width: `${from.width}px`, height: `${from.height}px`, objectFit: 'cover', zIndex: '60', pointerEvents: 'none', transition: 'transform 500ms cubic-bezier(.2,.8,.2,1), opacity 500ms' });
  ghost.removeAttribute('srcset');
  document.body.append(ghost);
  requestAnimationFrame(() => {
    ghost.style.transform = `translate(${to.left + 24 - from.left}px, ${to.top + 24 - from.top}px) scale(${64 / Math.max(from.width, 1)})`;
    ghost.style.transformOrigin = 'top left';
    ghost.style.opacity = '0.2';
  });
  setTimeout(() => ghost.remove(), 560);
}

function track(name, data) {
  try { window.Shopify?.analytics?.publish?.(name, data); } catch { /* analytics optional */ }
}

if (!customElements.get('s-style-goals')) {
  customElements.define('s-style-goals', SStyleGoals);
}
